import { useEffect, useState } from "react";
import { DialogTitle } from "../shared/modal";
import { SharedIcon } from "../shared/shared-icon";
import { ErrorContent } from "../shared/error-content";
import { LoadingContent } from "../shared/loading-content";
import { useSaveCollectionColoursModalStore } from "../../../js/stores/save-collection-colours-modal-store";
import {
  useCollectionSaveForm,
  useSaveCollection,
} from "../../../js/api/collection-save/collection-save";
import { useMainReact } from "../../main-react";
import { announce } from "../../../js/components/announce";
import { toast } from "../../../js/components/toast";

/**
 * One colour in the grid: a swatch, its name and code, and a tick-box.
 *
 * A REAL CHECKBOX INSIDE A LABEL, which is worth spelling out because it replaced a button with
 * `aria-pressed`. The design shows a distinct 32px tick-box under each swatch rather than the
 * swatch itself being the toggle, and a checkbox is what this actually is: one of a set of
 * independent selections, which is a different thing from a pressed button. Wrapping it all in the
 * label keeps the 124px swatch as the click target rather than asking anyone to hit the 32px box,
 * and screen readers get "Ember Glow, R253E, checkbox, checked" from the markup instead of a
 * hand-written aria attribute.
 *
 * The visible box is a sibling of the input, styled from `:checked` and `:focus-visible`, so the
 * state is never mirrored in JavaScript and cannot drift from what the input reports.
 */
function ColourTile({ colour, isSelected, onToggle }) {
  return (
    <label className="save-colours-tile">
      <span
        className="save-colours-tile__swatch"
        style={{ background: colour.colourRGB }}
        aria-hidden="true"
      />

      <span className="save-colours-tile__label">
        <span className="save-colours-tile__name">{colour.colourName}</span>
        <span className="save-colours-tile__code">{colour.colourCode}</span>
      </span>

      <input
        type="checkbox"
        className="save-colours-tile__input"
        checked={isSelected}
        onChange={() => onToggle(colour.colourCode)}
      />
      <span className="save-colours-tile__box" aria-hidden="true">
        <SharedIcon iconName="#sprite-icon-tick-only" className="save-colours-tile__tick" />
      </span>
    </label>
  );
}

export function SaveCollectionColoursContent({ closeClickHandler }) {
  const formUrl = useSaveCollectionColoursModalStore((state) => state.formUrl);
  const collectionId = useSaveCollectionColoursModalStore((state) => state.collectionId);

  const { generalServerErrorMessage, collectionSave } = useMainReact() ?? {};

  const { data, isLoading, isError } = useCollectionSaveForm(formUrl);
  const [selectedCodes, setSelectedCodes] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Everything starts selected. The common case by a distance is "give me this whole collection",
  // and the designs show it that way; deselecting the one shade you already own is a smaller job
  // than picking eight out of eight.
  useEffect(() => {
    if (!data?.items || selectedCodes !== null) return;

    setSelectedCodes(data.items.map((colour) => colour.colourCode));
  }, [data, selectedCodes]);

  const saveMutation = useSaveCollection();

  const toggleColour = (code) => {
    setSelectedCodes((previous) => {
      const next = new Set(previous ?? []);

      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }

      return [...next];
    });
  };

  const selected = selectedCodes ?? [];
  const isOverLimit = data?.maxColoursPerSave > 0 && selected.length > data.maxColoursPerSave;
  const canSubmit = selected.length > 0 && !isOverLimit;

  const submit = () => {
    if (!data?.mainAction?.linkUrl || !canSubmit) return;

    setIsSubmitting(true);

    saveMutation.mutate({
      url: data.mainAction.linkUrl,
      body: { collectionId, codes: selected },
      successHandler: (response) => {
        setIsSubmitting(false);

        if (!response?.isSuccess) {
          const message = response?.message ?? generalServerErrorMessage;
          toast.error(message);
          announce(message);
          return;
        }

        // Both, always. The toast is for people who can see it; the live region is the only way
        // anyone else learns the save happened, because nothing else on this page changes.
        toast.success(response.message);
        announce(response.message);

        // Tells the card that opened this modal to repaint its count without a page load.
        window.dispatchEvent(
          new CustomEvent("valspar:collection-saved", {
            detail: { collectionId, likesCount: response.likesCount },
          })
        );

        closeClickHandler();
      },
      errorHandler: (refusal) => {
        setIsSubmitting(false);

        const message = refusal.isRateLimited
          ? collectionSave?.rateLimitedMessage
          : refusal.message ?? generalServerErrorMessage;

        toast.error(message);
        announce(message);
      },
    });
  };

  if (isLoading || isSubmitting) {
    return <LoadingContent />;
  }

  if (isError) {
    return <ErrorContent />;
  }

  const isEmpty = !data?.items?.length;

  return (
    <div className="save-colours">
      <div className="save-colours__header">
        <DialogTitle className="save-colours__title">{data?.modalLabel}</DialogTitle>
      </div>

      {isEmpty && <div className="save-colours__empty">{data?.emptyStateText}</div>}

      {!isEmpty && (
        <>
          <div className="save-colours__grid">
            {data.items.map((colour) => (
              <ColourTile
                key={colour.colourCode}
                colour={colour}
                isSelected={selected.includes(colour.colourCode)}
                onToggle={toggleColour}
              />
            ))}
          </div>

          {isOverLimit && (
            <p className="save-colours__limit" role="alert">
              {collectionSave?.tooManyMessage}
            </p>
          )}

          <div className="save-colours__footer">
            <button
              type="button"
              className="saved-colors-action saved-colors-action--secondary save-colours__action"
              onClick={closeClickHandler}
            >
              {data?.secondaryAction?.linkLabel}
            </button>
            <button
              type="button"
              className="saved-colors-action saved-colors-action--main save-colours__action"
              onClick={submit}
              disabled={!canSubmit}
            >
              {/* The count is in the button because it is the one number the member is deciding
                  about, and the design puts it there: "Save (5) colours". */}
              {`${data?.mainAction?.linkLabel} (${selected.length})`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
